import { cn } from "../../lib/utils";
import { routes, type RouteId, isWorkBoardRoute } from "../../lib/routes";
import type { RouteConfig } from "../../lib/routes";
import { HugeiconsIcon } from "@hugeicons/react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home01Icon,
  TaskDaily02Icon,
  Route01Icon,
  Calendar03Icon,
  Target02Icon,
  Megaphone01Icon,
  UserMultiple02Icon,
  ChartLineData02Icon,
  CheckListIcon,
  Folder01Icon,
  Cancel01Icon,
} from "@hugeicons/core-free-icons";

const iconMap: Record<string, typeof Home01Icon> = {
  Home01Icon,
  TaskDaily02Icon,
  Route01Icon,
  Calendar03Icon,
  Target02Icon,
  Megaphone01Icon,
  UserMultiple02Icon,
  ChartLineData02Icon,
  CheckListIcon,
  Folder01Icon,
};

const COLLAPSED_WIDTH = 64;
const EXPANDED_WIDTH = 240;

const sidebarVariants = {
  collapsed: { width: COLLAPSED_WIDTH },
  expanded: { width: EXPANDED_WIDTH },
};

// ── Section grouping ─────────────────────────────────────────
// Command | Operations | Revenue | Resources
// "board" represents the entire Work Board (tabs: board, path, timeline, pipeline, checklist)
const NAV_SECTIONS: Array<{ label: string; routeIds: RouteId[] }> = [
  { label: "Command", routeIds: ["dashboard"] },
  { label: "Operations", routeIds: ["board", "channels"] },
  { label: "Revenue", routeIds: ["contacts", "scorecard"] },
  { label: "Resources", routeIds: ["assets"] },
];

/** Override labels for sidebar display (route.label stays for command palette) */
const SIDEBAR_LABEL_OVERRIDES: Partial<Record<RouteId, string>> = {
  board: "Work Board",
};

function getRouteConfig(id: RouteId): RouteConfig | undefined {
  return routes.find((r) => r.id === id);
}

interface NavRailProps {
  currentRoute: RouteId;
  onNavigate: (route: RouteId) => void;
  expanded: boolean;
  onToggleExpanded: () => void;
  onOpenSettings?: () => void;
}

export function NavRail({ currentRoute, onNavigate, expanded, onToggleExpanded, onOpenSettings }: NavRailProps) {
  return (
    <motion.nav
      initial={false}
      animate={expanded ? "expanded" : "collapsed"}
      variants={sidebarVariants}
      transition={{ type: "spring", stiffness: 400, damping: 30 }}
      className={cn(
        "h-full shrink-0 flex flex-col items-center",
        "border-r border-[var(--color-border-stroke)] bg-[var(--color-bg-sidebar)]",
        "pb-[var(--spacing-2)]",
        "overflow-hidden",
      )}
      aria-label="Main navigation"
    >
      {/* Header: Logo + Toggle — matches topbar height exactly */}
      <motion.div
        className="flex items-center shrink-0 h-[var(--topbar-height)] overflow-hidden border-b border-[var(--color-border-stroke)] px-[12px] w-full"
      >
        <button
          onClick={onToggleExpanded}
          className={cn(
            "h-[40px] w-[40px] rounded-[var(--radius-md)] flex items-center justify-center shrink-0",
            "cursor-pointer transition-colors duration-[120ms]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-bg-sidebar)]",
            expanded
              ? "text-[var(--color-icon)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)]"
              : "",
          )}
          aria-label={expanded ? "Collapse sidebar" : "Expand sidebar"}
        >
          <AnimatePresence mode="wait" initial={false}>
            {expanded ? (
              <motion.span
                key="close"
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="flex items-center justify-center"
              >
                <HugeiconsIcon icon={Cancel01Icon} size={18} />
              </motion.span>
            ) : (
              <motion.span
                key="logo"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="flex items-center justify-center w-[32px] h-[32px] rounded-[8px] bg-[var(--color-accent-500)]"
              >
                <svg width="20" height="7" viewBox="0 0 500 154" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: "block" }}>
                  <path d="M444.014 109.838C459.994 96.789 470.544 88.1743 490.01 86.0607C495.501 85.4645 500 81.0766 500 75.5538V10C500 4.47715 495.514 -0.0320518 490 0.268647C439.367 3.02949 410.814 26.4958 389.586 43.9411L389.543 43.9769L389.316 44.1621C370.802 59.28 359.577 68.4462 333.33 68.4462C306.981 68.4462 295.688 59.2884 277.122 43.9769L277.079 43.9411C254.492 25.3788 223.612 0 166.665 0C109.711 0 78.8242 25.3899 56.2417 43.9531L56.2127 43.9769L55.9683 44.1764C39.9941 57.2172 29.4483 65.8264 9.98995 67.9393C4.49938 68.5355 0 72.9233 0 78.4462L0 144C0 149.523 4.48577 154.032 10.0004 153.731C60.6328 150.971 89.1864 127.504 110.414 110.059L110.457 110.023L110.684 109.838C129.198 94.7199 140.423 85.5538 166.665 85.5538C193.019 85.5538 204.312 94.7116 222.878 110.023L222.907 110.047C245.489 128.61 276.376 154 333.335 154C390.289 154 421.176 128.61 443.758 110.047L443.787 110.023L444.014 109.838Z" fill="white"/>
                </svg>
              </motion.span>
            )}
          </AnimatePresence>
        </button>

        <AnimatePresence>
          {expanded && (
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15, delay: 0.05 }}
              className="ml-[var(--spacing-3)] text-[16px] font-semibold text-[var(--color-text-primary)] whitespace-nowrap select-none"
            >
              AI Wave
            </motion.span>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Spacer after header */}
      <div className="h-[var(--spacing-2)] shrink-0" />

      {/* Nav sections */}
      <div className="flex flex-col items-center w-full flex-1">
        {NAV_SECTIONS.map((section, sectionIdx) => (
          <div key={section.label} className="w-full flex flex-col items-center">
            {/* Section divider (between sections, not before first) */}
            {sectionIdx > 0 && (
              <motion.div
                className="flex flex-col items-center w-full"
                animate={{
                  paddingTop: expanded ? 2 : 6,
                  paddingBottom: expanded ? 2 : 6,
                }}
                transition={{ type: "spring", stiffness: 400, damping: 30 }}
              >
                <motion.div
                  className="border-b border-[var(--color-border-stroke)]"
                  animate={{
                    width: expanded ? EXPANDED_WIDTH - 24 : 24,
                    opacity: expanded ? 0 : 0.6,
                  }}
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                />
              </motion.div>
            )}

            {/* Section label (only when expanded) */}
            <AnimatePresence>
              {expanded && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 24 }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.15 }}
                  className="overflow-hidden"
                >
                  <motion.span
                    animate={{ width: EXPANDED_WIDTH - 24 }}
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                    className="flex items-center h-[24px] px-[var(--spacing-2)] text-[11px] font-semibold text-[var(--color-text-muted)] uppercase tracking-[0.06em] whitespace-nowrap select-none"
                  >
                    {section.label}
                  </motion.span>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Route items */}
            <motion.div
              className="flex flex-col items-center w-full"
              animate={{ gap: expanded ? 2 : 4 }}
              transition={{ type: "spring", stiffness: 400, damping: 30 }}
            >
              {section.routeIds.map((routeId, routeIdx) => {
                const route = getRouteConfig(routeId);
                if (!route) return null;
                // "board" item is active when ANY work board tab is the current route
                const isActive = routeId === "board"
                  ? isWorkBoardRoute(currentRoute)
                  : currentRoute === route.id;
                const displayLabel = SIDEBAR_LABEL_OVERRIDES[routeId] ?? route.label;
                const IconComponent = iconMap[route.iconName];
                // Global index for stagger delay
                const globalIdx = NAV_SECTIONS.slice(0, sectionIdx).reduce((s, sec) => s + sec.routeIds.length, 0) + routeIdx;

                return (
                  <motion.div
                    key={route.id}
                    className="flex justify-center"
                    animate={{ width: expanded ? EXPANDED_WIDTH - 24 : 40 }}
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  >
                    <button
                      onClick={() => onNavigate(route.id)}
                      className={cn(
                        "relative h-[40px] w-full rounded-[var(--radius-md)] flex items-center shrink-0",
                        "transition-colors duration-[120ms] cursor-pointer overflow-hidden",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-bg-sidebar)]",
                        isActive
                          ? "text-[var(--color-text-primary)]"
                          : "text-[var(--color-icon)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)]",
                      )}
                      aria-label={displayLabel}
                      aria-current={isActive ? "page" : undefined}
                    >
                      {/* Active indicator background */}
                      {isActive && (
                        <motion.div
                          layoutId="nav-active-bg"
                          className="absolute inset-0 rounded-[var(--radius-md)] bg-[var(--color-bg-hover)]"
                          transition={{ type: "spring", stiffness: 400, damping: 30 }}
                        />
                      )}

                      {/* Icon — always centered in a 40px zone */}
                      <span className="relative z-10 shrink-0 w-[40px] h-[40px] flex items-center justify-center">
                        {IconComponent && <HugeiconsIcon icon={IconComponent} size={20} />}
                      </span>

                      <AnimatePresence>
                        {expanded && (
                          <motion.span
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.15, delay: globalIdx * 0.02 }}
                            className="relative z-10 text-[14px] font-medium whitespace-nowrap"
                          >
                            {displayLabel}
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </button>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        ))}
      </div>

      {/* Settings button — pinned to bottom */}
      <div className="shrink-0 w-full flex flex-col items-center pb-[var(--spacing-2)]">
        <motion.div
          className="border-t border-[var(--color-border-stroke)] mb-[var(--spacing-2)]"
          animate={{ width: expanded ? EXPANDED_WIDTH - 24 : 40 }}
          transition={{ type: "spring", stiffness: 400, damping: 30 }}
        />
        <motion.div
          className="flex justify-center"
          animate={{ width: expanded ? EXPANDED_WIDTH - 24 : 40 }}
          transition={{ type: "spring", stiffness: 400, damping: 30 }}
        >
          <button
            onClick={onOpenSettings}
            className={cn(
              "relative h-[40px] w-full rounded-[var(--radius-md)] flex items-center shrink-0",
              "transition-colors duration-[120ms] cursor-pointer overflow-hidden",
              "text-[var(--color-icon)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)]",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-bg-sidebar)]",
            )}
            aria-label="Settings"
          >
            <span className="relative z-10 shrink-0 w-[40px] h-[40px] flex items-center justify-center">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </span>
            <AnimatePresence>
              {expanded && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className="relative z-10 text-[14px] font-medium whitespace-nowrap"
                >
                  Settings
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        </motion.div>
      </div>
    </motion.nav>
  );
}
